/**
 * The qualification checklist — opened when something tries to move a lead to
 * Qualified and the API answers `422 qualification_incomplete`.
 *
 * Mounted once, in `AppShell`, and driven by `ui.qualifyGate`, so it works the
 * same whether the status change came from the detail page, the list's inline
 * edit, the board, the edit form, or the "advance" button. The rule itself lives
 * in the API (`assertQualifiable` in `leads.service.js`); this dialog only
 * collects the answers and re-sends the status change with them, in one PATCH.
 * The completeness check below mirrors the API's so Save is only offered when it
 * would succeed, but the API stays the authority.
 */
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Modal, Field, Spinner } from '../../components/common/index.jsx';
import { ButtonSpinner } from '../../components/common/Loading.jsx';
import { closeQualifyGate, pushToast } from '../ui/uiSlice.js';
import { useLead, useUpdateLead } from './useLeads.js';

const BLANK = {
  requirement_summary: '', using_solution: null, current_solution: '', expected_quantity: '',
  has_budget: null, expected_budget_min: '', expected_budget_max: '', has_authority: null, decision_maker: '',
  expected_close_date: '', has_need: null,
  competitor_involved: null, competitor_name: '', location: '',
};

/** A Yes / No pair. `null` is "not answered yet", which is different from No. */
function YesNo({ value, onChange, name }) {
  return (
    <div className="d-flex gap-2" role="radiogroup" aria-label={name}>
      {[[true, 'Yes'], [false, 'No']].map(([v, label]) => (
        <button
          key={label}
          type="button"
          role="radio"
          aria-checked={value === v}
          className={`btn btn-sm-x ${value === v ? 'btn-add' : 'btn-soft'}`}
          onClick={() => onChange(v)}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

const filled = (v) => typeof v === 'string' && v.trim().length > 0;

/** Which questions are still unanswered — same rules as the API. */
function outstanding(f) {
  const out = [];
  if (!filled(f.requirement_summary)) out.push('requirement_summary');
  if (f.using_solution === null || (f.using_solution === true && !filled(f.current_solution))) out.push('using_solution');
  if (!filled(f.expected_quantity)) out.push('expected_quantity');
  if (f.has_budget === null) out.push('has_budget');
  if (f.has_budget === true) {
    const has = (v) => String(v).trim() !== '' && Number(v) >= 0;
    if (!has(f.expected_budget_min) || !has(f.expected_budget_max) || Number(f.expected_budget_max) < Number(f.expected_budget_min)) {
      out.push('expected_budget');
    }
  }
  if (f.has_authority === null || (f.has_authority === false && !filled(f.decision_maker))) out.push('has_authority');
  if (!f.expected_close_date) out.push('expected_close_date');
  if (f.has_need === null) out.push('has_need');
  if (f.competitor_involved === null || (f.competitor_involved === true && !filled(f.competitor_name))) out.push('competitor_involved');
  if (!filled(f.location)) out.push('location');
  return out;
}

const TOTAL = 9;

/** `<input type="date">` takes only `YYYY-MM-DD`; tolerate a Date or a full ISO timestamp too. */
function toDateInput(v) {
  if (!v) return '';
  if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v)) return v;
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export function QualifyGateModal() {
  const dispatch = useDispatch();
  const gate = useSelector((s) => s.ui.qualifyGate);
  const { data: lead, isLoading } = useLead(gate?.leadId);
  const update = useUpdateLead();
  const [form, setForm] = useState(BLANK);
  const [touched, setTouched] = useState(false);

  /* Prefill from what is already on the lead, so a rep who filled half of it
     earlier only answers what is left. */
  useEffect(() => {
    if (!gate || !lead) return;
    const q = lead.qualification ?? {};
    setForm({
      requirement_summary: q.requirement_summary ?? '',
      using_solution: q.using_solution ?? null,
      current_solution: q.current_solution ?? '',
      expected_quantity: q.expected_quantity ?? '',
      has_budget: q.has_budget ?? null,
      // A single figure saved before budgets were a range reads as a range of one value.
      expected_budget_min: [q.expected_budget_min, q.expected_budget].find((v) => typeof v === 'number')?.toString() ?? '',
      expected_budget_max: [q.expected_budget_max, q.expected_budget].find((v) => typeof v === 'number')?.toString() ?? '',
      has_authority: q.has_authority ?? null,
      decision_maker: q.decision_maker ?? '',
      expected_close_date: toDateInput(lead.expected_close_date),
      has_need: q.has_need ?? null,
      competitor_involved: q.competitor_involved ?? null,
      competitor_name: q.competitor_name ?? '',
      location: q.location ?? lead.address?.city ?? '',
    });
    setTouched(false);
  }, [gate?.leadId, lead?.id, lead?.row_version]); // eslint-disable-line react-hooks/exhaustive-deps

  const missing = useMemo(() => outstanding(form), [form]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setV = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const err = (k) => (touched && missing.includes(k) ? 'Required' : undefined);

  const close = () => dispatch(closeQualifyGate());

  const save = async () => {
    setTouched(true);
    if (missing.length || !lead) return;
    const patch = {
      // No status when the dialog was opened to edit the answers of an already-qualified lead.
      ...(gate.statusId ? { status_id: gate.statusId } : {}),
      requirement_summary: form.requirement_summary.trim(),
      has_budget: form.has_budget,
      has_authority: form.has_authority,
      has_need: form.has_need,
      expected_close_date: form.expected_close_date,
      qualification: {
        // Only meaningful with budget available; null clears a figure left from an earlier answer.
        expected_budget_min: form.has_budget ? Number(form.expected_budget_min) : null,
        expected_budget_max: form.has_budget ? Number(form.expected_budget_max) : null,
        using_solution: form.using_solution,
        // Cleared when the answer is No, so a stale name from an earlier answer does not linger.
        current_solution: form.using_solution ? form.current_solution.trim() : '',
        expected_quantity: form.expected_quantity.trim(),
        competitor_involved: form.competitor_involved,
        location: form.location.trim(),
        ...(form.has_authority === false ? { decision_maker: form.decision_maker.trim() } : {}),
        ...(form.competitor_involved ? { competitor_name: form.competitor_name.trim() } : {}),
      },
    };
    try {
      await update.mutateAsync({ id: lead.id, patch, rowVersion: lead.row_version, noGate: true });
      dispatch(pushToast({
        tone: 'success',
        message: gate.statusId ? `${lead.full_name ?? 'Lead'} is now Qualified` : 'Qualification answers saved',
      }));
      close();
    } catch { /* useUpdateLead already toasted the reason; the dialog stays open to fix it. */ }
  };

  const answered = TOTAL - missing.length;

  return (
    <Modal
      open={Boolean(gate)}
      onClose={close}
      title={gate?.statusId ? 'Qualify Lead' : 'Qualification Checklist'}
      size="lg"
      footer={(
        <>
          <span className="t-xs ink-3 me-auto">
            <i className="bi bi-info-circle me-1" aria-hidden="true" />
            {answered} of {TOTAL} answered{gate?.statusId ? ' — a lead can only be Qualified once the checklist is complete.' : '.'}
          </span>
          <button type="button" className="btn btn-cancel btn-sm-x" onClick={close}>Cancel</button>
          <button
            type="button"
            className="btn btn-save btn-sm-x"
            onClick={save}
            disabled={update.isPending || isLoading || !lead}
          >
            {update.isPending ? <><ButtonSpinner />Saving…</> : (gate?.statusId ? 'Mark as Qualified' : 'Save answers')}
          </button>
        </>
      )}
    >
      {isLoading || !lead ? (
        <div className="text-center py-4"><Spinner /></div>
      ) : (
        <>
          <div className="t-xs ink-3 mb-3">{lead.full_name} · {lead.lead_no}</div>

          <div className="eyebrow mb-2">Requirement</div>
          <div className="row g-3 mb-3">
            <div className="col-12">
              <Field label="1. What product/service is required?" required error={err('requirement_summary')}>
                <textarea className="form-control" rows={2} value={form.requirement_summary} onChange={set('requirement_summary')} />
              </Field>
            </div>
            <div className="col-md-6">
              <Field label="2. Is the lead using any solution?" required error={err('using_solution')}>
                <YesNo name="Using a solution" value={form.using_solution} onChange={setV('using_solution')} />
              </Field>
            </div>
            {form.using_solution === true && (
              <div className="col-md-6">
                <Field label="Current solution" required error={err('using_solution')}>
                  <input className="form-control" value={form.current_solution} onChange={set('current_solution')} placeholder="Product or vendor they use now" />
                </Field>
              </div>
            )}
            <div className="col-md-6">
              <Field label="3. Expected quantity?" required error={err('expected_quantity')}>
                <input className="form-control" value={form.expected_quantity} onChange={set('expected_quantity')} placeholder="e.g. 50 units" />
              </Field>
            </div>
            <div className="col-md-6">
              <Field label="7. Requirement confirmed?" required error={err('has_need')}>
                <YesNo name="Requirement confirmed" value={form.has_need} onChange={setV('has_need')} />
              </Field>
            </div>
            <div className="col-md-6">
              <Field label="9. Location?" required error={err('location')}>
                <input className="form-control" value={form.location} onChange={set('location')} />
              </Field>
            </div>
          </div>

          <div className="eyebrow mb-2">Buying</div>
          <div className="row g-3 mb-3">
            <div className="col-md-6">
              <Field label="4. Budget available?" required error={err('has_budget')}>
                <YesNo name="Budget" value={form.has_budget} onChange={setV('has_budget')} />
              </Field>
            </div>
            {form.has_budget === true && (
              <div className="col-md-6">
                <Field
                  label="Expected budget range"
                  required
                  error={err('expected_budget') && (Number(form.expected_budget_max) < Number(form.expected_budget_min)
                    ? 'The top of the range cannot be below the bottom.' : 'Enter both ends of the range')}
                >
                  <div className="input-group">
                    <span className="input-group-text">{lead.currency ?? 'USD'}</span>
                    <input
                      type="number" min="0" step="1000" className="form-control" aria-label="Budget from"
                      value={form.expected_budget_min} onChange={set('expected_budget_min')} placeholder="From, e.g. 12000"
                    />
                    <span className="input-group-text">–</span>
                    <input
                      type="number" min="0" step="1000" className="form-control" aria-label="Budget to"
                      value={form.expected_budget_max} onChange={set('expected_budget_max')} placeholder="To, e.g. 15000"
                    />
                  </div>
                </Field>
              </div>
            )}
            <div className="col-md-6">
              <Field label="6. Expected purchase / order date?" required error={err('expected_close_date')}>
                <input type="date" className="form-control" value={form.expected_close_date} onChange={set('expected_close_date')} />
              </Field>
            </div>
            <div className="col-md-6">
              <Field label="5. Speaking to the decision maker?" required error={err('has_authority')}>
                <YesNo name="Decision maker" value={form.has_authority} onChange={setV('has_authority')} />
              </Field>
            </div>
            {form.has_authority === false && (
              <div className="col-md-6">
                <Field label="Who is the decision maker?" required error={err('has_authority')}>
                  <input className="form-control" value={form.decision_maker} onChange={set('decision_maker')} />
                </Field>
              </div>
            )}
            <div className="col-md-6">
              <Field label="8. Competitor involved?" required error={err('competitor_involved')}>
                <YesNo name="Competitor" value={form.competitor_involved} onChange={setV('competitor_involved')} />
              </Field>
            </div>
            {form.competitor_involved === true && (
              <div className="col-md-6">
                <Field label="Which competitor?" required error={err('competitor_involved')}>
                  <input className="form-control" value={form.competitor_name} onChange={set('competitor_name')} />
                </Field>
              </div>
            )}
          </div>
        </>
      )}
    </Modal>
  );
}
